import {v4 as uuidv4} from 'uuid';

import {Selector} from 'notebook/ELNConstants';
import {
  LastSyncedUpstreamValue,
  SchemaFieldColumnUpdate,
  StatusFilter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {mergeColumnUpdatesIntoColumnConstraints} from 'notebook/tables/refresh-table-data/LegacyRefreshTableData';
import {RowMetadata, Table, TableData, TableOpDiff, TableOpDiffMap} from 'notebook/tables/TableConstants';
import {omit} from 'util/omitKey';

interface UpdateTableArgs {
  columnUpdates?: {[columnId: number]: SchemaFieldColumnUpdate | {}};
  updatedCells: Array<Array<Cell>>;
  lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
  updatedRowMetadatas?: Array<RowMetadata>;
  numApiRowsToAdd?: number;
  filters?: Array<StatusFilter>;
  tableOp?: TableOpDiff;
}

/**
 * A utility function that applies any column updates for a table, updates the cells of the table
 * and then calls a callbackFn with the updated table and any lastSyncedUpstreamValues which were
 * provided.
 *
 * @param table - the table to provide updates on
 * @param getIdFromCidFn - a function to give us the id for a given cid
 * @param callbackFn - our updateHandler for this table
 * @param getFilteredRowIds - a function to get the filtered row ids
 */
export default function updateTableWithColumnUpdatesAndCells(
  index: number,
  table: Table,
  getIdFromCidFn: (cid: string) => string | number | undefined,
  callbackFn: (
    index: number,
    content: {table: TableData; lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>},
    selectors?: Array<Selector>,
    tableOp?: TableOpDiffMap
  ) => void,
  getFilteredRowIds: (filters: Array<StatusFilter>) => Set<string>
): (updateTableArgs: UpdateTableArgs, selectors?: Array<Selector>) => void {
  return ({
    columnUpdates,
    updatedCells,
    lastSyncedUpstreamValuesByRow,
    updatedRowMetadatas,
    numApiRowsToAdd,
    filters,
    tableOp,
  }) => {
    const columnConstraints = columnUpdates
      ? mergeColumnUpdatesIntoColumnConstraints(table, columnUpdates, getIdFromCidFn)
      : table.columnConstraints;
    const updatedRowData = updatedRowMetadatas
      ? updatedRowMetadatas.map((rowMetadata) => ({
          rowId: rowMetadata.rowId ?? uuidv4(),
          isFinalized: rowMetadata.isFinalized ?? false,
          rowMetadata: omit(rowMetadata, 'rowId', 'isFinalized'),
        }))
      : table.rowData;
    const newTable = {
      ...table,
      table: updatedCells,
      // not every action functions provide filter updates, so only overwrite if defined
      ...(filters !== undefined && {
        filterInfo: {
          filters,
          filteredRowIds: Array.from(getFilteredRowIds(filters)),
        },
      }),
      rowData: updatedRowData,
      columnConstraints,
      numApiRowsToAdd,
    };
    callbackFn(index, {table: newTable, lastSyncedUpstreamValuesByRow}, undefined, {
      [table.name]: tableOp ?? {type: 'eval-table'}, // if table op is not specified, default to full table
    });
  };
}
